/**
 * OMR Card Layout 2x1 (2 cartões por página A4)
 * Renderiza 2 cartões OMR lado a lado em uma única página A4
 * Útil para imprimir como A5 (105mm × 148mm)
 *
 * Uso:
 * - Imprima normalmente (2 cartões por página A4)
 * - Corte ao meio
 * - Use como cartões A5
 */

import React, { useEffect } from 'react';
import OMRCardLayout from './OMRCardLayout';
import {
  PAGE_WIDTH_MM,
  PAGE_HEIGHT_MM,
  QUESTIONS_START_Y_MM,
} from '../../utils/omr/constants';
import { validateLayout } from '../../utils/omr/geometryCalc';
import styles from './OMRCardLayout.module.css';

const OMRCardLayout2x1 = ({
  examId = 1,
  examName = 'Exame de Avaliação',
  questions = [],
  studentIdFormat = 'cpf',
  alternativesPerQuestion = 5,
}) => {
  // Validate layout on mount
  useEffect(() => {
    validateLayout(questions.length, QUESTIONS_START_Y_MM);
  }, [questions.length]);

  // Dimensões para 2 cartões em A4
  const cardWidthMm = PAGE_WIDTH_MM / 2; // 105mm (A5 width)
  const cardHeightMm = PAGE_HEIGHT_MM; // 297mm (A4 height, cada card tem altura cheia)

  return (
    <div
      style={{
        width: '100%',
        height: 'auto',
        display: 'flex',
        flexWrap: 'wrap',
        backgroundColor: '#fff',
      }}
    >
      {/* Cartão 1 - Esquerda */}
      <div
        style={{
          width: `${cardWidthMm}mm`,
          height: `${cardHeightMm}mm`,
          display: 'inline-block',
          overflow: 'hidden',
          borderRight: '1px dashed #ccc',
          pageBreakInside: 'avoid',
        }}
      >
        <div style={{ transform: 'scale(0.5)', transformOrigin: '0 0', width: '200%', height: '200%' }}>
          <OMRCardLayout
            examId={examId}
            examName={examName}
            questions={questions}
            studentIdFormat={studentIdFormat}
            alternativesPerQuestion={alternativesPerQuestion}
          />
        </div>
      </div>

      {/* Cartão 2 - Direita */}
      <div
        style={{
          width: `${cardWidthMm}mm`,
          height: `${cardHeightMm}mm`,
          display: 'inline-block',
          overflow: 'hidden',
          pageBreakInside: 'avoid',
        }}
      >
        <div style={{ transform: 'scale(0.5)', transformOrigin: '0 0', width: '200%', height: '200%' }}>
          <OMRCardLayout
            examId={examId}
            examName={examName}
            questions={questions}
            studentIdFormat={studentIdFormat}
            alternativesPerQuestion={alternativesPerQuestion}
          />
        </div>
      </div>

      {/* Instruções de impressão */}
      <div
        style={{
          width: '100%',
          padding: '10mm',
          backgroundColor: '#f5f5f5',
          fontSize: '10pt',
          color: '#666',
          borderTop: '1px solid #ddd',
          pageBreakBefore: 'avoid',
          pageBreakInside: 'avoid',
        }}
      >
        <strong>Instruções de Impressão (A5 - 2 em 1):</strong>
        <ul style={{ marginTop: '5mm', marginBottom: '5mm' }}>
          <li>Imprima em tamanho A4 (100%)</li>
          <li>Cada página contém 2 cartões lado a lado</li>
          <li>Corte ao meio (linha tracejada) para obter 2 cartões A5</li>
          <li>Use cartões A5 (105mm × 148mm) para os alunos preencherem</li>
        </ul>
        <strong>Dicas:</strong>
        <ul style={{ marginTop: '5mm' }}>
          <li>Use papel 120g+ para melhor qualidade</li>
          <li>Imprima em 300 DPI para melhor OMR detection</li>
          <li>Mantenha as margens para os fiduciais (cantos pretos)</li>
        </ul>
      </div>
    </div>
  );
};

export default OMRCardLayout2x1;
